import { useCallback } from 'react';
import { useSelector } from 'react-redux';

import { reportError } from 'helpers/error-logging';
import { IA_KEY } from 'helpers/ia-logic';
import { selectIaLogic } from 'selectors/general';

import useModifierMutations from './useModifierMutations';
import useModifiersReadOnly from './useModifiersReadOnly';

export function useModifierLinkAccess() {
  const { isReadOnly } = useModifiersReadOnly();
  const { featureIsSupported } = useSelector(state => selectIaLogic(state, IA_KEY.SKU_MODIFIERS));

  return { canAuthorModifierLinks: !isReadOnly && featureIsSupported, canViewModifierLinks: featureIsSupported };
}

export default function useModifierItemLinks({ venueId }) {
  const { canAuthorModifierLinks, canViewModifierLinks } = useModifierLinkAccess();
  const { linkGroupToItems, unlinkGroupFromItem } = useModifierMutations({ venueId });

  const submitGroupLinksForItem = useCallback(
    async ({ itemId, linked = [], unlinked = [] }) => {
      if (!canAuthorModifierLinks || (linked.length === 0 && unlinked.length === 0)) {
        return { failedGroupIds: [], linkedGroupIds: [], unlinkedGroupIds: [] };
      }

      const operations = [
        ...linked.map(groupId => ({
          groupId,
          run: () => linkGroupToItems.mutateAsync({ groupId, itemIds: [itemId] }),
        })),
        ...unlinked.map(groupId => ({ groupId, run: () => unlinkGroupFromItem.mutateAsync({ groupId, itemId }) })),
      ];
      const linkResults = await Promise.allSettled(operations.map(operation => operation.run()));
      const linkFailures = linkResults.filter(result => result.status === 'rejected');
      if (linkFailures.length > 0) {
        reportError('Some modifier group links failed', { failures: linkFailures.map(failure => failure.reason) });
      }

      const succeeded = kindIds =>
        kindIds.filter(groupId => {
          const index = operations.findIndex(operation => operation.groupId === groupId);
          return linkResults[index].status === 'fulfilled';
        });

      return {
        failedGroupIds: operations
          .filter((operation, index) => linkResults[index].status === 'rejected')
          .map(operation => operation.groupId),
        linkedGroupIds: succeeded(linked),
        unlinkedGroupIds: succeeded(unlinked),
      };
    },
    [canAuthorModifierLinks, linkGroupToItems, unlinkGroupFromItem]
  );

  return { canAuthorModifierLinks, canViewModifierLinks, submitGroupLinksForItem };
}
